/**
 * renderTemplate.js — tiny, dependency-free template engine for CPG AI Lab prompts.
 *
 * Prompt templates are plain strings stored in data/ai-lab/promptLibrary.js so they can
 * later be moved into MongoDB and edited by an admin without touching code.
 *
 * Supported syntax
 * ----------------
 *   {{field}}                 → value (trimmed). Empty → "" (surrounding whitespace collapses)
 *   {{field|list}}            → comma/newline-separated value rendered as "- item" bullet lines
 *   {{field|numbered}}        → same, rendered as "1. item" lines
 *   {{field|join}}            → "A, B and C"
 *   {{field|upper}}           → UPPERCASE
 *   {{field|article}}         → "an energy drink" / "a protein bar"
 *   {{field|lower}}           → lowercase
 *   {{field|default:Some text}} → fallback when the field is empty
 *   {{#if field}} ... {{/if}}         → block shown only when field has a value
 *   {{#if field=Value}} ... {{/if}}   → block shown only when field equals Value (case-insensitive)
 *   {{#if field!=Value}} ... {{/if}}  → block shown only when field differs from Value
 *   {{#unless field}} ... {{/unless}} → block shown only when field is empty
 *   {{#each field}}- {{this}}\n{{/each}} → iterate comma/newline-separated value
 *
 * Blocks may be nested. Unknown fields render as empty strings (never "undefined").
 */

export function splitList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (value === null || value === undefined) return [];
  return String(value)
    .split(/\r?\n|,|;|•/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function hasValue(value) {
  if (Array.isArray(value)) return value.some((v) => String(v).trim().length > 0);
  if (typeof value === "boolean") return value;
  if (value === null || value === undefined) return false;
  return String(value).trim().length > 0;
}

function joinNatural(items) {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function applyFilter(value, filter) {
  const [name, ...rest] = filter.split(":");
  const arg = rest.join(":");
  switch (name.trim()) {
    case "list":
      return splitList(value).map((i) => `- ${i}`).join("\n");
    case "numbered":
      return splitList(value).map((i, idx) => `${idx + 1}. ${i}`).join("\n");
    case "join":
      return joinNatural(splitList(value));
    case "upper":
      return String(value ?? "").toUpperCase();
    case "lower":
      return String(value ?? "").toLowerCase();
    case "article": {
      // "energy drink" → "an energy drink"; "protein bar" → "a protein bar"
      const v = String(value ?? "").trim().toLowerCase();
      if (!v) return "";
      return `${/^[aeiou]/.test(v) ? "an" : "a"} ${v}`;
    }
    case "default":
      return hasValue(value) ? String(value).trim() : arg;
    default:
      return hasValue(value) ? String(value).trim() : "";
  }
}

// {{#if field}} … {{/if}}   |   {{#if field=Some value}} … {{/if}}   |   {{#if field!=Some value}} … {{/if}}
const OPEN_RE = /\{\{#(if|unless|each)\s+([\w.-]+)(?:(!=|=)([^}]*))?\}\}/;
const TAG_RE = /\{\{(#|\/)(if|unless|each)\b[^}]*\}\}/g;

function evaluate(value, op, expected) {
  if (!op) return hasValue(value);
  const actual = String(value ?? "").trim().toLowerCase();
  const want = String(expected ?? "").trim().toLowerCase();
  return op === "=" ? actual === want : actual !== want;
}

/**
 * Find the index of the {{/kind}} that closes the block opened at `openEnd`,
 * counting nested openers of the same kind so {{#if}} inside {{#if}} works.
 * Returns { closeStart, closeEnd } or null if unbalanced.
 */
function findClose(template, kind, openEnd) {
  TAG_RE.lastIndex = openEnd;
  let depth = 1;
  let m;
  while ((m = TAG_RE.exec(template)) !== null) {
    if (m[2] !== kind) continue;
    depth += m[1] === "#" ? 1 : -1;
    if (depth === 0) return { closeStart: m.index, closeEnd: m.index + m[0].length };
  }
  return null;
}

function renderBlocks(template, data) {
  let out = template;
  let match;
  while ((match = OPEN_RE.exec(out)) !== null) {
    const [full, kind, field, op, expected] = match;
    const openEnd = match.index + full.length;
    const close = findClose(out, kind, openEnd);
    if (!close) {
      // Unbalanced block — strip the opener so we never loop forever or leak syntax into the prompt.
      out = out.slice(0, match.index) + out.slice(openEnd);
      continue;
    }
    const body = out.slice(openEnd, close.closeStart);
    const value = data[field];
    const truthy = evaluate(value, op, expected);
    let replacement = "";
    if (kind === "if") replacement = truthy ? renderBlocks(body, data) : "";
    if (kind === "unless") replacement = truthy ? "" : renderBlocks(body, data);
    if (kind === "each") {
      replacement = splitList(value)
        .map((item, index) => renderBlocks(body, { ...data, this: item, index: index + 1 }))
        .join("");
    }
    out = out.slice(0, match.index) + replacement + out.slice(close.closeEnd);
  }
  return out;
}

function renderVars(template, data) {
  return template.replace(/\{\{\s*([\w.-]+)(?:\|([^}]+))?\s*\}\}/g, (_, field, filter) => {
    const value = data[field];
    if (filter) return applyFilter(value, filter);
    return hasValue(value) ? String(value).trim() : "";
  });
}

function tidy(text) {
  return text
    .replace(/[ \t]+\n/g, "\n") // trailing spaces
    .replace(/\n{3,}/g, "\n\n") // collapse 3+ blank lines
    .trim();
}

/**
 * Render a template string with a data object.
 * @param {string} template
 * @param {Record<string, any>} data
 */
export function renderTemplate(template, data = {}) {
  if (!template) return "";
  const withBlocks = renderBlocks(template, data);
  return tidy(renderVars(withBlocks, data));
}

/**
 * Build the data object for a task from (1) the user's form answers and (2) their brand profile.
 * Form answers always win; brand profile fills any field whose `brandKey` matches.
 */
export function buildTaskData(task, formValues = {}, brandProfile = null) {
  const data = {};
  for (const field of task.fields || []) {
    let value = formValues[field.id];
    if (!hasValue(value) && brandProfile && field.brandKey && hasValue(brandProfile[field.brandKey])) {
      value = brandProfile[field.brandKey];
    }
    if (!hasValue(value) && field.default !== undefined) value = field.default;
    data[field.id] = value ?? "";
  }
  return data;
}

/**
 * Quick validation — returns an array of { fieldId, message } for missing required fields.
 */
export function validateTask(task, data) {
  const errors = [];
  for (const field of task.fields || []) {
    if (field.required && !hasValue(data[field.id])) {
      errors.push({ fieldId: field.id, message: `${field.label} is required` });
    }
  }
  return errors;
}

/**
 * Produce a "preview" version of a prompt where unfilled fields show as [Field label] placeholders.
 * Used for the Copy Prompt button on toolkit pages when no brand profile exists.
 */
export function renderWithPlaceholders(task, brandProfile = null) {
  const data = buildTaskData(task, {}, brandProfile);
  for (const field of task.fields || []) {
    if (!hasValue(data[field.id])) data[field.id] = `[${field.label}]`;
  }
  return renderTemplate(task.template, data);
}
